/**
 * visual-review: two visuals compared in place. `MediaSlider` stacks them and reveals one over the
 * other with a range input (keyboard: arrows, Home/End; touch: drag); `MediaSplit` puts them side
 * by side. Both take rendered nodes (an `<img>`, a `<video>`, a sandboxed frame), so the same
 * component serves before/after artifacts and design-option compare. No decrypted bytes are
 * handled here.
 */

import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import './media-compare.css'

export type CompareMode = 'side' | 'slider'

export function MediaSplit({
  left,
  right,
  leftLabel,
  rightLabel,
}: {
  left: ReactNode
  right: ReactNode
  leftLabel: ReactNode
  rightLabel: ReactNode
}) {
  return (
    <div className="mc-split">
      <figure className="mc-split__cell">
        <figcaption>{leftLabel}</figcaption>
        <div className="mc-media">{left}</div>
      </figure>
      <figure className="mc-split__cell">
        <figcaption>{rightLabel}</figcaption>
        <div className="mc-media">{right}</div>
      </figure>
    </div>
  )
}

export function MediaSlider({
  before,
  after,
  beforeLabel,
  afterLabel,
}: {
  before: ReactNode
  after: ReactNode
  beforeLabel: string
  afterLabel: string
}) {
  const id = useId()
  const [pos, setPos] = useState(50)
  return (
    <div className="mc-slider" data-position={pos}>
      <div className="mc-slider__stage">
        <div className="mc-media mc-slider__base">{after}</div>
        <div
          className="mc-media mc-slider__top"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          aria-hidden="true"
        >
          {before}
        </div>
        <span className="mc-slider__tag mc-slider__tag--before" aria-hidden="true">
          Before
        </span>
        <span className="mc-slider__tag mc-slider__tag--after" aria-hidden="true">
          After
        </span>
        <div className="mc-slider__rule" style={{ left: `${pos}%` }} aria-hidden="true">
          <span className="mc-slider__handle" />
        </div>
        {/* The range covers the stage: drag anywhere on the image; arrows, Home/End by key. */}
        <input
          className="mc-slider__range"
          type="range"
          min={0}
          max={100}
          step={1}
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          aria-label={`Reveal ${beforeLabel} over ${afterLabel}`}
          aria-valuetext={`${pos}% ${beforeLabel}`}
          aria-describedby={`${id}-b ${id}-a`}
        />
      </div>
      <div className="mc-slider__bar">
        <span id={`${id}-b`}>{beforeLabel}</span>
        <span id={`${id}-a`}>{afterLabel}</span>
      </div>
    </div>
  )
}

/** Mode switch (side by side / slider). The slider is offered only for stills. */
export function CompareModeSwitch({
  mode,
  onChange,
  sliderOk,
  label,
}: {
  mode: CompareMode
  onChange: (m: CompareMode) => void
  sliderOk: boolean
  label: string
}) {
  return (
    <div className="mc-modes" role="radiogroup" aria-label={label}>
      {(
        [
          ['slider', 'Slider'],
          ['side', 'Side by side'],
        ] as const
      ).map(([m, text]) => (
        // biome-ignore lint/a11y/useSemanticElements: segmented control, not a form radio
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={mode === m}
          disabled={m === 'slider' && !sliderOk}
          title={m === 'slider' && !sliderOk ? 'The slider works for images' : undefined}
          className={`mc-modes__b${mode === m ? ' is-on' : ''}`}
          onClick={() => onChange(m)}
        >
          {text}
        </button>
      ))}
    </div>
  )
}
